import { AlertTriangle, DollarSign, Lock, Tractor, Users } from "lucide-react";
import type { DailyEntrySummary } from "@crewledger/shared/schemas";
import { FormHint, StatCard } from "@/components/design";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { MICROCOPY } from "@/lib/microcopy";

interface DailyEntrySummaryPanelProps {
  summary: DailyEntrySummary;
  canViewPayroll: boolean;
  isLocked: boolean;
  payPeriodLabel: string | null;
}

export function DailyEntrySummaryPanel({
  summary,
  canViewPayroll,
  isLocked,
  payPeriodLabel,
}: DailyEntrySummaryPanelProps) {
  return (
    <aside className="sticky top-4 space-y-3">
      <div className="rounded-xl border-2 border-navy-blueprint/20 bg-navy-deep text-white shadow-[var(--shadow-elevated)]">
        <div className="border-b border-white/10 px-4 py-3">
          <p className="text-xs uppercase tracking-widest text-white/60">Site board</p>
          <h2 className="text-lg font-semibold">Day totals</h2>
          {payPeriodLabel ? (
            <p className="text-xs text-white/70">{payPeriodLabel}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2 p-3">
          <div className="rounded-lg bg-white/10 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">Workers</p>
            <p className="text-2xl font-bold tabular-nums">{summary.workerCount}</p>
          </div>
          <div className="rounded-lg bg-white/10 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">Total hours</p>
            <p className="text-2xl font-bold tabular-nums">{formatHours(summary.totalHours)}</p>
          </div>
          <div className="rounded-lg bg-white/5 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">Regular</p>
            <p className="text-lg font-semibold tabular-nums">{formatHours(summary.regularHours)}</p>
          </div>
          <div className="rounded-lg bg-white/5 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">OT</p>
            <p className="text-lg font-semibold tabular-nums text-amber-300">
              {formatHours(summary.overtimeHours)}
            </p>
          </div>
          <div className="rounded-lg bg-white/5 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">DT</p>
            <p className="text-lg font-semibold tabular-nums text-orange-300">
              {formatHours(summary.doubleTimeHours)}
            </p>
          </div>
          <div className="rounded-lg bg-white/5 px-3 py-2">
            <p className="text-[10px] uppercase text-white/60">Equip hrs</p>
            <p className="text-lg font-semibold tabular-nums">{formatHours(summary.equipmentHours)}</p>
          </div>
        </div>
        <div className="space-y-2 border-t border-white/10 px-4 py-3">
          {canViewPayroll ? (
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm text-white/80">
                <DollarSign className="h-4 w-4" />
                Est. gross pay
              </span>
              <span className="font-mono text-lg font-bold text-orange-construction">
                {formatCurrency(summary.estimatedGrossPay ?? 0)}
              </span>
            </div>
          ) : null}
          {canViewPayroll ? (
            <div className="flex items-center justify-between text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <Tractor className="h-4 w-4" />
                Equipment cost
              </span>
              <span className="font-mono">{formatCurrency(summary.equipmentCost ?? 0)}</span>
            </div>
          ) : null}
          {canViewPayroll ? (
            <FormHint className="text-white/50">{MICROCOPY.equipmentCostSeparate}</FormHint>
          ) : null}
          {summary.exceptionCount > 0 ? (
            <div className="flex items-start gap-2 rounded-lg bg-red-500/20 px-3 py-2 text-sm text-red-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {summary.exceptionCount} exception{summary.exceptionCount === 1 ? "" : "s"} to resolve
            </div>
          ) : null}
          {isLocked ? (
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/20 px-3 py-2 text-sm text-amber-100">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">Pay period locked</p>
                <p className="mt-0.5 text-xs opacity-90">{MICROCOPY.lockedPayPeriod}</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid gap-2">
        <StatCard label="Crew on board" value={String(summary.workerCount)} icon={Users} accent="navy" />
      </div>
    </aside>
  );
}
